"use client";

import React, { useCallback } from 'react';
import { GoogleMap, OverlayView, useLoadScript } from '@react-google-maps/api';
import { isFilled } from '@prismicio/helpers';
import { dashify } from '@/app/lib/utils';
import { RevealTitle } from '@/app/lib/RevealTitle';
import { mapStyles } from '@/app/lib/mapStyle';
import sharedStyles from '@/app/shared.module.scss';
import styles from './venuesMap.module.scss';

const containerStyle = {
	width: '100%',
	height: '50rem',
};

const options = {
	styles: mapStyles,
	disableDefaultUI: true,
	zoomControl: true,
};

const TooltipContent = ({ venue }) => {
	if (!venue?.data) return null;
	return (
		<div className={styles.tooltip}>
			{isFilled.keyText(venue?.data?.name) && (
				<div className={styles.tooltip_name}>{venue.data.name}</div>
			)}
		</div>
	);
};

const CustomMarker = ({ color, index, venue }) => {
	const scrollToVenueCard = () => {
		const id = venue?.data?.name ? dashify(venue.data.name) : null;
		if (id) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
	};
	return (
		<div
			style={{ backgroundColor: color || '#77F252' }}
			className={styles.marker}
			role="button"
			tabIndex={0}
			onClick={scrollToVenueCard}
			onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { scrollToVenueCard(); e.preventDefault(); } }}
			aria-label={`Scroll to ${venue?.data?.name || 'venue'}`}
		>
			<TooltipContent venue={venue} />
			{index}
		</div>
	);
};

const VenuesMap = ({ slice, pageData }) => {
	const { isLoaded, loadError } = useLoadScript({
		googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
	});

	const getMapCenter = useCallback(() => {
		const venues = pageData?.data?.venue;
		if (!venues || venues.length === 0) {
			return { lat: 51.5074, lng: -0.1278 };
		}
		let latSum = 0, lngSum = 0, validCount = 0;
		venues.forEach(({ venue }) => {
			const lat = venue?.data?.geolocation?.latitude;
			const lng = venue?.data?.geolocation?.longitude;
			if (lat && lng) {
				latSum += lat;
				lngSum += lng;
				validCount++;
			}
		});
		return validCount > 0 ? { lat: latSum / validCount, lng: lngSum / validCount } : { lat: 51.5074, lng: -0.1278 };
	}, [pageData?.data?.venue]);

	const onLoad = useCallback(function callback(mapInstance) {
		const bounds = new window.google.maps.LatLngBounds();
		pageData?.data?.venue.forEach(({ venue }) => {
			const lat = venue?.data?.geolocation?.latitude;
			const lng = venue?.data?.geolocation?.longitude;
			if (lat && lng) {
				bounds.extend({ lat, lng });
			}
		});
		if (!bounds.isEmpty()) {
			mapInstance.fitBounds(bounds);
		} else {
			mapInstance.setCenter(getMapCenter());
			mapInstance.setZoom(10);
		}
	}, [pageData?.data?.venue, getMapCenter]);

	if (loadError) return "Error loading maps";
	if (!isLoaded) return "Loading Maps...";

	return (
		<div id="venues-map" className={styles.wrapper}>
			<div className={sharedStyles.top}>
				<div className={sharedStyles.left}>
					{isFilled.keyText(slice?.primary?.title) && (
						<h1 className={sharedStyles.h1} id={dashify(slice?.primary?.title)}>
							<RevealTitle size='small'>{slice?.primary?.title}</RevealTitle>
						</h1>
					)}
				</div>
			</div>
			<div className={styles.main}>
				<GoogleMap
					mapContainerStyle={containerStyle}
					center={getMapCenter()}
					zoom={10}
					options={options}
					onLoad={onLoad}
				>
					{pageData?.data?.venue?.map(({ venue }, index) => {
						const lat = venue?.data?.geolocation?.latitude;
						const lng = venue?.data?.geolocation?.longitude;
						const color = venue?.data?.background_color;

						if (lat && lng) {
							return (
								<OverlayView
									key={index}
									position={{ lat, lng }}
									mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
								>
									<CustomMarker
										color={color}
										index={index + 1}
										venue={venue}
									/>
								</OverlayView>
							);
						}
						return null;
					})}
				</GoogleMap>
			</div>
		</div>
	);
};

export default VenuesMap;